/**
 * 거래소백엔드 관리자 계약(`~/dev1/sjo-exchange-backend/docs/10_API계약.md` 5절·7절)의
 * **canonical 값과 응답 모양**, 그리고 화면이 쓰는 한국어 표시명(명세 4-2절).
 *
 * ⚠ **canonical 값은 절대 번역하지 않는다.** 버튼 키·상태·등급·감사 action 은
 * 서버가 그 문자열로 판단한다. 화면에 「리스크 관리자」를 그리는 것과 전선에 그 글자를
 * 흘려보내는 것은 다른 일이다 — 후자를 한 번 하면 그때부터 철자가 둘이 된다.
 *
 * ⚠ **금액은 내부 단위 정수 문자열이다**(계약 0-4). 1 USDE = 100. `"100500"` 은
 * 1,005.00 USDE 다. 이 파일의 `Units` 는 모두 그 모양이고, 숫자로 바꾸지 않는다.
 * 정해지지 않은 값은 `null` 이다 — 0 이 아니다.
 *
 * ⚠ **시각은 UTC epoch 밀리초 정수다**(계약 0-5).
 *
 * 표시명은 이 파일의 맨 아래에서 **한 곳에서만** 만든다(명세 4-1).
 */

/** UTC epoch 밀리초 (계약 0-5). */
export type Millis = number

/** USDE 내부 단위 정수 문자열 (계약 0-4). 1 USDE = 100. */
export type Units = string

/** `page`·`size` 방식 쪽 나누기의 답 (계약 0-6). */
export interface Paged<T> {
  items: T[]
  page: number
  size: number
  total: number
}

/* ── 버튼 권한 키 (계약 7절) ─────────────────────────────────────────── */

/**
 * 관리자 API 하나에 키 하나. 권한 이름마다 이 키들을 최고관리자가 고른다.
 *
 * ⚠ **유니온으로 적는 이유는 오타 때문이다.** 문자열로 다루면 `members.veiw` 가
 * 조용히 통과해 메뉴 하나가 영원히 안 보인다 — 그리고 그것은 「권한이 없나 보다」로
 * 읽힌다.
 */
export const BUTTON_KEYS = [
  'members.view',
  'members.grade_history.view',
  'members.status.edit',
  'members.grade.edit',
  'members.kyc.view',
  'signups.view',
  'signups.review',
  'partner_applications.view',
  'partner_applications.review',
  'revocations.request',
  'revocations.view',
  'revocations.decide',
  'collateral.view',
  'capacity.review',
  'withdrawals.view',
  'withdrawals.approve',
  'trades.view',
  'incidents.view',
  'incidents.handle',
  'risk.view',
  'risk.rules.edit',
  'risk.evaluate',
  'risk_queue.view',
  'risk_queue.edit',
  'wallets.view',
  'wallets.risk.view',
  'audit.view',
  'settlements.view',
  'archives.view',
  'retention.view',
  'retention.edit',
  'retention.decide',
  'settings.view',
  'settings.edit',
  'terms.view',
  'terms.edit',
  'escrow_setup.view',
  'escrow_setup.run',
] as const

export type ButtonKey = (typeof BUTTON_KEYS)[number]

/* ── 5-1 로그인·자기 정보·직원·권한 이름 ──────────────────────────────── */

export type AdminStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'

/** 감사 기록 조회 범위 (계약 XM-06·XM-20, 거래소 01 6-16). */
export type AuditScope = 'ALL' | 'OWN_AND_RISK_INCIDENT' | 'OWN_OPERATIONS'

export interface PermissionNameRef {
  id: number
  name: string
}

/**
 * XM-01 의 답 — 세션 답.
 *
 * 직원 OTP 는 정할 것 39(사장님 2026-10-13 「관리자 웹에 직원 OTP가 왜 있냐」)로 없앴다 — OTP 티켓 칸을 보지 않는다.
 */
export interface AdminSession {
  token?: string | null
  expiresAt?: Millis | null
  pinRequired?: boolean
  hasPin?: boolean
  /** 첫 설정(XM-03)이 남았으면 true. */
  setupRequired?: boolean
}

/**
 * XM-06 — 모든 화면이 메뉴·버튼을 그리는 근거.
 *
 * ⚠ **역할 이름·권한 이름으로 짐작하지 않는다.** 메뉴와 버튼은 `superAdmin`·`keys`
 * 로만 그린다(명세 0-5). `keys` 는 서버가 아는 키를 그대로 받으므로 `string[]` 이다 —
 * 이 웹이 아직 모르는 키가 와도 버리지 않는다.
 */
export interface AdminMe {
  adminId: number
  loginId: string
  superAdmin: boolean
  /** 직원의 권한 이름. 최고관리자는 null. */
  permissionName: PermissionNameRef | null
  keys: string[]
  auditScope: AuditScope
  status: AdminStatus
  pinVerified: boolean
  hasPin: boolean
  setupRequired: boolean
  sessionExpiresAt: Millis | null
}

/** XM-13 의 한 줄. */
export interface StaffRow {
  adminId: number
  loginId: string
  email: string
  superAdmin: boolean
  permissionName: PermissionNameRef | null
  status: AdminStatus
  hasPin: boolean
  setupRequired: boolean
  loginLocked: boolean
  lastLoginAt: Millis | null
  createdAt: Millis
}

/** XM-14 의 답. */
export interface CreatedStaff {
  adminId: number
  loginId: string
  email: string
  permissionName: PermissionNameRef | null
  status: AdminStatus
}

/** XM-18 의 한 페이지 — 7절 표 그대로. */
export interface PermissionPage {
  page: string
  label: string
  buttons: { key: string; label: string; legacyCode: string | null }[]
}

/** XM-19 의 한 줄. XM-20·XM-21 의 답도 이 모양이다. */
export interface PermissionNameRow {
  id: number
  name: string
  keys: string[]
  auditScope: AuditScope
  staffCount: number
  updatedAt: Millis | null
  updatedByAdminId: number | null
}

/** XM-20·XM-21 의 본문. XM-21 의 `keys` 는 전체를 덮어쓴다. */
export interface PermissionNameInput {
  name: string
  keys: string[]
  auditScope: AuditScope
}

/* ── 5-2 회원·가입·파트너 심사·마스터 박탈 ───────────────────────────── */

export type MemberGrade = 'MASTER' | 'SPECIAL' | 'GENERAL'
export type MemberStatus = 'ACTIVE' | 'SUSPENDED' | 'WITHDRAWN'
export type KycStatus = 'NOT_STARTED' | 'PENDING' | 'VERIFIED' | 'REJECTED'
export type KycProvider = 'SUMSUB' | 'TEMPORARY'

/** 가입·파트너 신청의 심사 상태. */
export type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED'
/**
 * XM-30 가입 신청 상태 — 2026-10-12(물음 16 ⑤ — 가입 때 KYC)부터. `KYC_REVIEW` SUMSUB 심사 중(통과하면 저절로 승인),
 * `KYC_REJECTED` SUMSUB 최종 거절(신청자의 [수동심사요청] 기다림), `MANUAL_REVIEW` 수동심사 — 관리자가 정함.
 */
export type SignupStatus = 'KYC_REVIEW' | 'KYC_REJECTED' | 'MANUAL_REVIEW' | 'APPROVED' | 'REJECTED'
/** XM-30 `kyc.status` — 가입 신원 인증의 상태. */
export type SignupKycStatus = 'STARTED' | 'UNDER_REVIEW' | 'RESUBMIT' | 'VERIFIED' | 'REJECTED'

/**
 * XM-23·XM-24·XM-26 — 거래가 끝나면 정지(사장님 2026-10-02 「거래 정지 기준 … 거래 정지 대상자가 거래중이라면 거래완료 후
 * 정지」). 거래 중인 회원을 정지하면 그 거래(`tradeId`)가 완료·취소될 때 정지가 걸린다 — 그때까지 `status` 는 `ACTIVE`.
 */
export interface PendingSuspension {
  tradeId: string
  requestedAt: Millis
}

/** XM-23 의 한 줄. */
export interface MemberRow {
  memberId: number
  loginId: string
  email: string
  grade: MemberGrade
  status: MemberStatus
  kycStatus: KycStatus
  masterId: number | null
  walletAddress: string | null
  createdAt: Millis
  withdrawnAt: Millis | null
  /** 거래가 끝나면 정지 — 없으면 null. */
  pendingSuspension: PendingSuspension | null
}

/** XM-24. */
export interface MemberDetail {
  memberId: number
  loginId: string
  email: string
  grade: MemberGrade
  status: MemberStatus
  kycStatus: KycStatus
  kycVerifiedAt: Millis | null
  masterId: number | null
  masterLoginId: string | null
  walletAddress: string | null
  createdAt: Millis
  withdrawnAt: Millis | null
  partner: {
    applicationStatus: string | null
    partnerStatus: string | null
    approvedAt: Millis | null
    activatedAt: Millis | null
    collateralContract: string | null
    referralCode: string | null
    warningLevel: number | null
  } | null
  collateral: {
    partnerId: number
    partnerStatus: string | null
    contract: string | null
    balance: Units | null
    balanceReadAt: Millis | null
    balanceLive: boolean
  } | null
  /** 거래가 끝나면 정지 — 없으면 null. */
  pendingSuspension: PendingSuspension | null
  /**
   * 지갑 정지(2026-10-02, 거래소 01 2-24 — 지갑 백엔드가 알린 등록 지갑의 정지, 재단 홈페이지 요청). 회원 `status` 와 따로다.
   * `suspension_pending` = 거래가 끝나면 지갑 정지, `suspended` = 지갑 정지됨. 없거나 풀렸으면 null.
   */
  walletSuspension: WalletSuspension | null
  /**
   * 패널티 잠금(2026-10-29 사장님 앱 수정 D — 사고처리 중 거래를 관리자가 거래취소로 끝내면 그 거래의 구매자를 잠근다. 관리자가 풀 때까지
   * 로그인·조회만). 회원 `status` 와 따로다. 옛 서버면 칸이 없다.
   */
  penaltyLock?: PenaltyLock | null
}

/** XM-24 `penaltyLock` — 잠기지 않았으면 `locked` false 이고 나머지는 null. */
export interface PenaltyLock {
  locked: boolean
  lockedAt: Millis | null
  /** 잠근 거래(관리자가 거래취소한 거래) 번호. */
  orderId: string | null
  /** 그 거래취소를 요청한 관리자. */
  lockedByAdminId: number | null
  lockedByAdminLoginId: string | null
  walletAddress: string | null
}

/** XM-24 `walletSuspension`. */
export interface WalletSuspension {
  status: 'suspension_pending' | 'suspended'
  at: Millis
}

/** 등급 이력의 사유 (거래소백엔드 `GradeChangeReason`, 명세 4-2). */
export type GradeChangeReason =
  | 'REFERRAL_CODE_REGISTERED'
  | 'MASTER_APPROVED_JOIN'
  | 'MASTER_EXPELLED'
  | 'ADMIN_EXPELLED'
  | 'PARTNER_APPROVED'
  | 'WITHDRAWAL_APPROVED'
  | 'UNPAID_APPROVAL_CANCELLED'
  | 'MASTER_REVOKED'
  | 'DIRECT_CHANGE'
  | 'CAPACITY_DEPOSIT_CHANGED'

/** XM-25 의 한 줄. */
export interface GradeHistoryRow {
  id: number
  memberId: number
  previousGrade: MemberGrade | null
  newGrade: MemberGrade
  previousMasterId: number | null
  newMasterId: number | null
  reasonType: GradeChangeReason | string
  reason: string | null
  masterId: number | null
  relatedIncidentId: number | null
  relatedOrderId: string | null
  /** ⚠ null 이 정상이다 — 회원 본인 행위와 마스터의 조직 행위에는 승인자가 없다. */
  approvedByAdminId: number | null
  batchId: string | null
  createdAt: Millis
}

/** XM-29. 2026-10-12 — 새 가입으로 만든 회원은 그 가입 신원 인증을 보인다(`signupApplicationId`·`approvedBy`). */
export interface MemberKyc {
  memberId: number
  status: KycStatus
  provider: KycProvider | null
  applicantId: string | null
  reviewAnswer: string | null
  reviewRejectType: string | null
  rejectLabels: string[] | null
  startedAt: Millis | null
  verifiedAt: Millis | null
  reviewedAt: Millis | null
  signupApplicationId?: number | null
  approvedBy?: 'KYC' | 'ADMIN' | null
}

/**
 * XM-30·XM-31 `kyc` — 가입 신청의 신원 인증(2026-10-12). 목록은 앞 여섯 칸, 상세(XM-31)는 나머지도.
 * `moderationComment` 는 신청자에게 보여도 되는 글, `clientComment` 는 보이면 안 되는 글(SUMSUB 문서).
 */
export interface SignupKyc {
  provider: KycProvider
  status: SignupKycStatus
  applicantId: string | null
  reviewAnswer: string | null
  reviewRejectType: string | null
  rejectLabels: string[] | null
  externalUserId?: string | null
  reviewStatus?: string | null
  moderationComment?: string | null
  clientComment?: string | null
  startedAt?: Millis | null
  reviewedAt?: Millis | null
  updatedAt?: Millis | null
}

/** XM-31 `results` — 그 인증에 받은 SUMSUB 결과 한 줄. */
export interface SignupKycResult {
  source: 'WEBHOOK' | 'STATUS_CHECK'
  type: string | null
  reviewAnswer: string | null
  reviewRejectType: string | null
  outcome: string
  sumsubTime: string | null
  receivedAt: Millis | null
}

/** XM-30 의 한 건. XM-32·XM-33 의 답도 이 모양이다. XM-31 은 `results` 를 더한다. */
export interface SignupApplication {
  applicationId: number
  loginId: string
  email: string
  referralCode: string | null
  status: SignupStatus
  rejectReason: string | null
  referralResult: string | null
  memberId: number | null
  reviewedByAdminId: number | null
  reviewedAt: Millis | null
  createdAt: Millis
  approvedBy: 'KYC' | 'ADMIN' | null
  manualReviewRequestedAt: Millis | null
  kyc: SignupKyc | null
  results?: SignupKycResult[]
}

/** XM-123 — 신청자가 SUMSUB 에 낸 것(단계마다). */
export interface KycDocuments {
  available: boolean
  reason: string | null
  steps: KycStep[]
}

export interface KycStep {
  step: string
  idDocType: string | null
  country: string | null
  reviewAnswer: string | null
  reviewRejectType: string | null
  rejectLabels: string[] | null
  moderationComment: string | null
  clientComment: string | null
  images: KycImage[]
}

export interface KycImage {
  imageId: string
  reviewAnswer: string | null
  reviewRejectType: string | null
  rejectLabels: string[] | null
  moderationComment: string | null
  clientComment: string | null
}

/** XM-34 의 한 건. XM-37 의 답도 이 모양이다. */
export interface PartnerApplication {
  applicationId: number
  memberId: number
  loginId: string
  status: ReviewStatus
  gradeAtApply: MemberGrade | null
  masterIdAtApply: number | null
  termsVersion: number | null
  termsAgreedAt: Millis | null
  phoneVerificationId: number | null
  appliedAt: Millis
  reviewedAt: Millis | null
  reviewedByAdminId: number | null
  rejectReason: string | null
  /** 신청 때 고른 단계(표가 바뀌어도 그대로 — 확인 31). 옛 신청은 없음. api-XB XM-34. */
  tier?: TierChoice | null
  /** 신청 즉시 지갑에 보낸 계약 생성 가스료 송금 요청(확인 21·24·57). 옛 신청은 없음. */
  gas?: ApplicationGas | null
}

/** 마스터가 고른 단계 — 정원·설정 금액(원문 2, 확인 31). */
export interface TierChoice {
  tierId?: number
  members: number
  /** 설정 금액(내부 단위). */
  deposit: Units
}

/** 파트너 신청 가스료 송금 요청(api-XB XA-44 `gas`). 상태 값은 충전 요청과 같다. */
export interface ApplicationGas {
  requestId: string
  amount: Units
  toAddress: string
  status: string
  expiresAt: Millis | null
  confirmedAt: Millis | null
}

/** XM-36 의 답 — 승인은 미예치 상태로 끝난다. */
export interface PartnerApproved {
  applicationId: number
  partnerId: number
  partnerStatus: string
  firstDepositDeadline: Millis
}

export type RevocationStatus = 'REQUESTED' | 'APPROVED' | 'REJECTED'

/** 박탈 보기 (XM-38 의 답, XM-39 의 한 줄, XM-41·XM-42 의 답). */
export interface Revocation {
  revocationId: number
  memberId: number
  loginId: string
  grade: MemberGrade | null
  status: RevocationStatus
  reason: string
  riskReason: string | null
  relatedIncidentId: number | null
  relatedOrderId: string | null
  requestedByAdminId: number
  requestedAt: Millis
  decidedByAdminId: number | null
  decidedAt: Millis | null
  rejectReason: string | null
  /** ⚠ 결정 전에는 null — 0 이 아니다. */
  demotedCount: number | null
  batchId: string | null
  /** `REQUESTED` 일 때만 [memberId·loginId]. 그 밖은 null. */
  specials: { memberId: number; loginId: string }[] | null
}

/**
 * 보증금 정산 값 — 철회(XM-47·XM-48)의 `requested`·`recomputed`·`approved`, 박탈(XM-40)의
 * `recomputed`. 사용료가 미정이면 `feesDue`·`refund` 가 null 이다.
 */
export interface SettlementValues {
  balance: Units | null
  feesDue: Units | null
  penalty: Units | null
  refund: Units | null
}

/** XM-40 — 박탈 보기 + 정산 예상(계약 고침 3회차). */
export interface RevocationDetail extends Revocation {
  /** 승인하면 보증금을 정산하는지. `REQUESTED` 가 아니면 null. */
  settles: boolean | null
  /** 박탈 정산 예상. `REQUESTED` 이고 `settles` 일 때만. 체인을 못 읽으면 null. */
  recomputed: SettlementValues | null
}

/** XM-38 의 본문. */
export interface RevocationInput {
  memberId: number
  reason: string
  riskReason?: string | undefined
  relatedIncidentId?: number | undefined
  relatedOrderId?: string | undefined
}

/* ── 5-3 보증금·담보·철회·정원 변경 ───────────────────────────────────── */

export type PartnerStatus =
  | 'AWAITING_DEPOSIT'
  | 'ACTIVE'
  | 'WITHDRAWAL_PENDING'
  | 'WITHDRAWN'
  | 'REVOKED'
  | 'CANCELLED_UNPAID'

/** XM-43 의 한 줄. XM-44 `partner` 도 이 모양이다. */
export interface PartnerRow {
  partnerId: number
  memberId: number
  loginId: string
  status: PartnerStatus | string
  seq: number | null
  approvedAt: Millis | null
  firstDepositDeadline: Millis | null
  activatedAt: Millis | null
  feeStartDate: string | null
  referralCode: string | null
  capacityDeposit: Units | null
  maxCapacityDeposit: Units | null
  suspended: boolean
  /** 0 없음 · 1 · 2 · 3 정지 (정지면 3). */
  warningLevel: number
  /** 1/100 퍼센트 — `1234` 는 12.34%. */
  drainedBp: number | null
  collateralContract: string | null
  contractStatus: string | null
  lastBalance: Units | null
  lastBalanceReadAt: Millis | null
  endedAt: Millis | null
  /** 이 마스터의 설정 단계(R-XB-02). 새 판 전 기록이면 없음. */
  tier?: TierChoice | null
  /** 넣은 합(체인 예치 기록의 합, R-XB-03). */
  depositedTotal?: Units | null
  /** 월 사용료(R-XB-06). */
  monthlyFee?: MonthlyFee | null
}

/**
 * 월 사용료 — 월 10,000 USDE 선납(원문 4, 확인 13·16·19). api-XB XA-46 `monthlyFee`.
 *
 * 2026-10-12 (사장님 2026-10-11 「사용료차감일날 거래횟수 수수료도 같이 차감되는 월 사용료다」 「과금구간은 100건당 10usde」 —
 * 거래소백엔드 V542): `amount` = 고정 부분(`fee.monthly`). 거래 횟수 수수료 — 지금 기간(`periodStart` 부터)의 회원 완료 거래
 * `periodTradeCount` 건, 그 수수료 `periodTradeFee`(지금 요율: 시작한 `tradeBlockSize` 건마다 `tradeBlockFee`), 고정 + 그것
 * `nextAmount`(기간이 지금 끝나면 다음 차감 금액 — 거래가 늘면 늘어남), 마지막으로 시작한 달의 나눔 `lastMonth`. 옛 서버면 없다.
 */
export interface MonthlyFee {
  amount: Units
  /** 이미 낸 기간의 끝 = 다음 차감 시각(멈춘 시간만큼 늘어남 — 확인 19). */
  paidThrough: Millis | null
  nextDeductionAt: Millis | null
  /** 멈춘 시간(밀리초)의 합. */
  pausedMs: number
  tradeBlockSize?: number | null
  tradeBlockFee?: Units | null
  periodStart?: Millis | null
  periodTradeCount?: number | null
  periodTradeFee?: Units | null
  nextAmount?: Units | null
  lastMonth?: FeeMonthParts | null
}

/** XA-46 `monthlyFee.lastMonth` — 마지막으로 시작한 달과 그 나눔(첫 달은 거래 칸이 0·요율 null). */
export interface FeeMonthParts {
  seq: number
  periodStart: Millis | null
  chainDate: string | null
  fixedFee: Units
  tradeCount: number
  tradeFee: Units
  total: Units
  status: string
  tradesFrom: Millis | null
  tradeBlockSize: number | null
  tradeBlockFee: Units | null
  paidAt: Millis | null
  via: string | null
}

/** XM-44 `collateral` — 거래소 앱 XA-46 과 같은 모양. */
export interface PartnerCollateral {
  partnerId: number
  partnerStatus: string
  collateralContract: string | null
  contractStatus: string | null
  referralCode: string | null
  members: { held: number; max: number | null }
  balance: { amount: Units | null; readAt: Millis | null; live: boolean }
  /** 옛 날마다 사용료 — 월 사용료(R-XB-06)로 바뀌며 없어졌다. 옛 서버면 올 수 있다. */
  todayFee?: {
    date: string
    fixed: Units | null
    additional: Units | null
    tradeCount: number | null
    total: Units | null
  } | null
  /** 이 마스터의 설정 단계(설정 금액 = deposit — 확인 31·32). */
  tier?: TierChoice | null
  /** 넣은 합(R-XB-03 — 활성 판정). */
  depositedTotal?: Units | null
  /** 월 사용료(R-XB-06). */
  monthlyFee?: MonthlyFee | null
  /** 지금 표에서 한 단계 위. shortfall = 새 단계 금액 − 지금 잔액(서버 계산). */
  nextTier?: { members: number; deposit: Units; shortfall: Units | null } | null
  feePeriodStart: string | null
  nextDeductionDate: string | null
  firstDepositDeadline: Millis | null
  minFirstDeposit: Units | null
  firstDepositShortfall: Units | null
  capacityDeposit: Units | null
  maxCapacityDeposit: Units | null
  floorAmount: Units | null
  suspended: boolean
  suspendedReason: string | null
  drainedBp: number | null
  warning: { level: number; show: boolean; message: string | null }
}

/**
 * XM-44 의 체인 작업 한 줄. 발견-410(2026-10-15): `attempts` 는 서명해 보낸 횟수(서명 전에 막힌 시도 — 가스 면제 상한 등 — 는
 * 세지 않음), `failures` 는 실패해 다시 기다린 횟수, `nextTryAt` 은 실패 뒤 기다리는 동안(PENDING) 이 시각 전에는 다시 하지 않음
 * (아니면 null). `errorName`·`lastError` 는 마지막 실패 — 이름은 나간 뒤에도 남는다(지난 실패). 새 두 칸은 옛 서버 답에 없다.
 */
export interface ChainOperation {
  id: number
  kind: string
  status: string
  txHash: string | null
  attempts: number
  failures?: number
  nextTryAt?: Millis | null
  errorName: string | null
  lastError: string | null
  result: unknown
  createdAt: Millis
  sentAt: Millis | null
  finishedAt: Millis | null
}

export interface CollateralCharge {
  requestId: string
  amount: Units
  status: string
  expiresAt: Millis | null
  resultStatus: string | null
  resultTxHash: string | null
  confirmedAt: Millis | null
  confirmedAmount: Units | null
  createdAt: Millis
}

/** XM-44. */
export interface PartnerDetail {
  partner: PartnerRow
  collateral: PartnerCollateral
  salt: string | null
  label: string | null
  codeId: number | null
  wasmAdmin: string | null
  mismatchDetail: string | null
  chainOperations: ChainOperation[]
  charges: CollateralCharge[]
}

export type WithdrawalStatus = 'PENDING' | 'APPROVED' | 'SUPERSEDED'

/** XM-47 의 한 줄. XM-48·XM-49 의 답도 이 모양이다. */
export interface Withdrawal {
  withdrawalId: number
  partnerId: number
  memberId: number
  loginId: string
  status: WithdrawalStatus | string
  requestedAt: Millis
  approvalDeadlineAt: Millis | null
  overdue: boolean
  /** 신청 때 계산한 값. */
  requested: SettlementValues | null
  /** XM-48 에서만 — 지금 잔액으로 다시 계산. 목록은 null, 체인을 못 읽어도 null. */
  recomputed: SettlementValues | null
  approvedAt: Millis | null
  approvedByAdminId: number | null
  approved: SettlementValues | null
  demotedCount: number | null
  batchId: string | null
  /** 그 파트너의 마지막 정산(SETTLE) 체인 작업. 새 칸(`attempts`·`failures`·`nextTryAt`·`lastError` — 발견-410)은 ChainOperation 과 같은 뜻, 옛 서버 답에는 없다. */
  settlement: {
    status: string | null
    txHash: string | null
    attempts?: number
    failures?: number
    nextTryAt?: Millis | null
    errorName: string | null
    lastError?: string | null
    result: unknown
  } | null
}

export type CapacityDirection = 'INCREASE' | 'DECREASE'
export type CapacityChangeStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'

/** XM-50 의 한 줄. XM-51·XM-52 의 답도 이 모양이다. */
export interface CapacityChange {
  requestId: number
  partnerId: number
  memberId: number
  loginId: string
  direction: CapacityDirection
  currentAmount: Units | null
  targetAmount: Units
  targetCapacity: number | null
  status: CapacityChangeStatus
  requestedAt: Millis
  reviewedAt: Millis | null
  reviewedByAdminId: number | null
  rejectReason: string | null
}

/* ── 5-4 거래·사고·위험 ───────────────────────────────────────────── */

export type TradeStage =
  | 'awaiting_signatures'
  | 'awaiting_proof'
  | 'proof_partial'
  | 'inspecting'
  | 'incident'
  | 'completing'
  | 'completed'
  | 'cancelled'

export type DepositType = 'start_sell' | 'start_buy'
export type TradeSource = 'APP' | 'EGGPAY'

/** 관리자 거래 보기 `TradeView` (계약 0-9). */
export interface TradeView {
  orderId: string
  stage: TradeStage | string
  depositType: DepositType | string
  source: TradeSource | string
  generalMemberId: number | null
  callMemberId: number | null
  sellerMemberId: number | null
  buyerMemberId: number | null
  sellerAddress: string | null
  buyerAddress: string | null
  amount: Units
  fee: Units | null
  depositAmount: Units | null
  matchedAt: Millis | null
  signDeadline: Millis | null
  paymentDeadline: Millis | null
  extendsUsed: number | null
  closedAt: Millis | null
  result: string | null
  cancellationReason: string | null
  cancellationDetail: string | null
  /**
   * 취소로 끝난 까닭 `ADMIN`(관리자 거래취소)·`BOTH_PARTIES`(두 사람 취소)·`SIGN_TIMEOUT`·`SIGN_REFUSED`, 아니면 null
   * (2026-10-01 새 칸 — 거래소백엔드 f9a1767). 앞 판 서버면 칸이 없다.
   */
  cancelCause?: string | null
}

export type IncidentStatus = 'OPEN' | 'PROCESSING' | 'CLOSED'
/**
 * 관리자가 사고 거래에 보낼 수 있는 처리(계약 XM-61) — 거래완료(`CONFIRM` = 에스크로 Confirm)·
 * 거래취소(`CANCEL` = 에스크로 Cancel) 둘만이다. 거래 흐름 04 9단계(2026-10-01) 사장님 원문 9
 * 「관리자페이지에서 거래완료 또는 거래취소상태로 스마트컨트렉트 상태를 변경할 수 있다」 — 앞 판의
 * 사고 판정 승인·거부(`RESOLVE_APPROVE`·`RESOLVE_REJECT`)·만료 처리(`EXPIRE`)는 서버가 400 으로 막는다.
 */
export type IncidentAction = 'CONFIRM' | 'CANCEL'

/** 사고 보기 `IncidentCase` (계약 0-9). */
export interface IncidentCase {
  id: number
  /** 거래 번호(T…). */
  orderId: string
  cause: string
  status: IncidentStatus
  sellerMemberId: number | null
  buyerMemberId: number | null
  amount: Units | null
  assigneeAdminId: number | null
  assigneeLoginId: string | null
  pendingAction: string | null
  openedAt: Millis
  closedAt: Millis | null
  closeResult: string | null
  /**
   * 2026-10-01 새 칸(거래소백엔드 da01075 — 사장님 「1번으로 하는데 거래 시간 항목이 왜없지」): 거래 시각(매칭 시각),
   * 회원이 앱에서 보는 주문 번호(O…), 두 사람 아이디. 거래 행이 없으면 null.
   */
  tradeAt: Millis | null
  orderNo: string | null
  sellerLoginId: string | null
  buyerLoginId: string | null
}

export interface IncidentEvent {
  id: number
  kind: string
  adminId: number | null
  adminLoginId: string | null
  toAdminId: number | null
  handoverTo: string | null
  action: string | null
  text: string | null
  at: Millis
  /**
   * 처리 요청(`ACTION_REQUESTED`) 줄의 오프라인 증빙(2026-10-01 새 칸 — 거래소백엔드 999cd8f): 확인한 기관·담당자·
   * 확인 날짜(yyyy-mm-dd)·문서번호·문서 파일(XM-115 문서 번호). 다른 줄·그 전 처리 줄은 null.
   */
  institution?: string | null
  officer?: string | null
  confirmedOn?: string | null
  documentNo?: string | null
  documentId?: number | null
}

/** 증빙 판독값 — 금액은 영수증에서 읽은 **원화 원문**이다. USDE 로 바꾸지 않는다. */
export interface IncidentProof {
  proofId: number
  role: 'seller' | 'buyer' | string
  round: number
  source: string | null
  direction: string | null
  amount: string | number | null
  whenText: string | null
  accountHash: string | null
  amountCandidateHashes: string[] | null
  hasPhoto: boolean
  receivedAt: Millis
  /**
   * 2026-10-01 새 칸(거래소백엔드 1b4268b·f9a1767 — 판독 규칙): 한 장으로 읽혔는지(못 읽은 사진도 남음), 판독 결과
   * `waiting`·`clear`·`anomaly`·`amount_mismatch`·`unreadable`(그 전 사진은 null), 까닭 종류, 사고처리 중 다시 올린
   * 사진인지. 앞 판 서버면 칸이 없다.
   */
  readable?: boolean
  result?: ProofResult | string | null
  resultReason?: string | null
  duringIncident?: boolean
}

/** 사진 한 장의 판독 결과(XM-58 `proofs[].result`·XM-117 `photos[].result`). */
export type ProofResult = 'waiting' | 'clear' | 'anomaly' | 'amount_mismatch' | 'unreadable'

export interface IncidentVerdict {
  status: 'clear' | 'anomaly' | string | null
  reason: string | null
  requestedRound: number | null
  verdictRound: number | null
  receivedAt: Millis | null
}

/**
 * XM-58 의 답 — 열 때마다 감사 기록이 남는다.
 *
 * ⚠ 거래 쪽을 못 읽으면(`tradeUnavailable`) 서버가 `proofs`·`verdicts`·`timeline`·`termsConsents`·`chat` 을 null 로
 * 준다(거래소백엔드 `IncidentService.openDetail`). 화면은 빈 목록으로 읽는다.
 */
export interface IncidentDetail {
  incident: IncidentCase
  events: IncidentEvent[]
  trade: TradeView | null
  proofs: IncidentProof[] | null
  verdicts: IncidentVerdict[] | null
  /** 거래 쪽을 못 읽은 까닭. 읽었으면 null. */
  tradeUnavailable: string | null
  /** 이 사고의 거래 자료 메일(XM-114) 기록, 오래된 것 먼저(2026-10-01 거래 흐름 04 8단계). */
  materialMails: IncidentMaterialMail[]
  /** 단계 기록·체인 거래 번호(2026-10-01 새 칸 — 거래소백엔드 f9a1767). */
  timeline?: TradeTimeline | null
  /** 두 사람이 동의한 지갑거래서명약관 판·시각(2026-10-01 새 칸). */
  termsConsents?: TermsConsents | null
  /** 거래 대화(2026-10-01 새 칸 — 거래소백엔드 96d06a6). 대화방이 열린 적이 없으면 null. */
  chat?: TradeChat | null
  /** 이 사고에 올린 문서(XM-115), 오래된 것 먼저(2026-10-01 새 칸 — 거래소백엔드 999cd8f). */
  documents?: IncidentDocument[]
}

/**
 * XM-58 `timeline` — 거래가 닿은 단계마다 그 시각(오래된 것 먼저)과 예치·종료 체인 거래 번호. 거래 자료 메일에
 * 붙는 값과 같다. 단계 이름은 거래 단계(`TradeStage`) 그대로.
 */
export interface TradeTimeline {
  orderId: string
  /** 회원이 앱에서 보는 주문 번호(O…). */
  orderNo: string | null
  stages: { stage: TradeStage | string; at: Millis }[]
  /** 판매자 예치 거래 번호. 없으면 null. */
  depositTxHash: string | null
  /** 거래를 끝낸 에스크로 거래 번호(완료 Confirm·취소 Cancel·취소 뒤 예치 반환). 없으면 null. */
  closingTxHash: string | null
}

/** XM-58 `termsConsents` — 판매자·구매자가 동의한 지갑거래서명약관 판과 동의 시각(지갑이 판을 안 보냈거나 서명 전이면 null). */
export interface TermsConsents {
  sellerVersion: number | null
  sellerAgreedAt: Millis | null
  buyerVersion: number | null
  buyerAgreedAt: Millis | null
}

/**
 * XM-58 `chat` — 사고처리 중인 거래의 대화(읽기만). 거래가 끝나면(거래완료·거래취소) 서버가 곧바로 지워 `open` false·
 * `messages` 빈 목록이다(사장님 2026-10-01 「사고처리되면 즉시 제거한다.」).
 */
export interface TradeChat {
  open: boolean
  messages: { id: number; sender: 'system' | 'seller' | 'buyer' | string; text: string; at: Millis }[]
}

/** XM-115 의 답·XM-58 `documents` 의 한 줄 — 사고에 올린 공문 사진·PDF(내용은 XM-116 으로 본다). */
export interface IncidentDocument {
  id: number
  fileName: string
  contentType: string
  /** 바이트. */
  size: number
  adminId: number | null
  adminLoginId: string | null
  uploadedAt: Millis
}

/** 거래 자료 메일을 받을 당사자(계약 XM-114 `role`). */
export type MaterialMailRole = 'seller' | 'buyer'

/**
 * 보낸 자료 메일의 언어(XM-58 `materialMails[].language` — 기록). 2026-10-01 기록은 `ko`·`en`, 2026-10-02 부터는 늘 `en`
 * (사장님 「일단 영어로만」 — 거래소백엔드 1ab5d45, 보내는 창에는 언어 고르기가 없다), 그 전 기록은 null.
 */
export type MailLanguage = 'ko' | 'en'

/**
 * XM-117 의 답 — [자료 메일 보내기] 창을 채우는 것(받는 사람마다). 아무것도 보내거나 적지 않는다.
 * `subject`·`body` 는 에디터에 채우는 템플릿 글(영어 — 관리자가 고칠 수 있음), `materials` 는 본문 뒤에 붙을 거래
 * 자료(DB 값 — 고칠 수 없음, 에디터 밖에 글로만), `photos` 는 그 회원 자기 사진(오래된 것 먼저). 2026-10-02 부터 영어로만
 * — `language` 칸이 없어졌다(거래소백엔드 1ab5d45).
 */
export interface MaterialMailPreview {
  role: MaterialMailRole | string
  templateVersion: number
  subject: string
  body: string
  materials: string
  photos: MaterialMailPhoto[]
}

/** XM-117 `photos` 의 한 장 — 거래 안 사진 번호·올린 시각·판독 결과(영어 글)·사진 파일이 있어 붙는지. */
export interface MaterialMailPhoto {
  proofId: number
  round: number
  uploadedAt: Millis
  result: ProofResult | string | null
  resultLabel: string
  attached: boolean
}

/** `SENT` 보냄 · `FAILED` 메일 서버가 받지 않음 · `SENDING` 보내는 중(서버가 그 사이 멈췄을 때만 남음). */
export type MaterialMailStatus = 'SENT' | 'FAILED' | 'SENDING'

/**
 * 거래 자료 메일 한 건(XM-114 의 답, XM-58 `materialMails` 의 한 줄) — 거래소백엔드
 * `IncidentMaterialMail` 그대로. 누가(`adminId`)·언제·누구에게(역할·회원·보낸 주소)·어느 템플릿 판으로·
 * 무엇을(보낸 제목·본문, 붙인 증빙 번호) 보냈는지. 시각은 밀리초.
 */
export interface IncidentMaterialMail {
  id: number
  incidentId: number
  orderId: string
  role: MaterialMailRole | string
  memberId: number
  memberLoginId: string | null
  /** 보낸 주소(그 회원의 가입 이메일). 사고 상세 문 뒤에서만 온다. */
  email: string
  templateVersion: number
  /** 보낸 언어 `ko`·`en`(2026-10-01 새 칸 — 거래소백엔드 5df107e, 2026-10-02 부터 늘 `en`). 그 전 기록은 null. */
  language: MailLanguage | string | null
  /**
   * 실제로 나간 제목·본문(관리자가 보낸 것 그대로). 나간 메일 본문 = `body` + 빈 줄 + `materials`. 그 전 기록의
   * `body` 는 템플릿 본문 + 거래 자료이고 `materials` 는 null.
   */
  subject: string
  body: string
  /** 본문 뒤에 붙여 나간 거래 자료 글(서버가 DB 에서 만든 것, 2026-10-01 새 칸). 그 전 기록은 null. */
  materials: string | null
  /** 첨부한 증빙 사진(그 회원 자신의 것). */
  proofIds: number[]
  status: MaterialMailStatus | string
  /** `FAILED` 의 오류 코드(예 `mail_unavailable`). */
  failure: string | null
  adminId: number
  adminLoginId: string | null
  createdAt: Millis
  finishedAt: Millis | null
}

export type RiskLevel = 'CAUTION' | 'SUSPICIOUS' | 'HIGH_RISK'
export type RiskQueueStatus =
  | 'NEW'
  | 'UNDER_REVIEW'
  | 'ESCALATED'
  | 'ON_HOLD'
  | 'RESTRICTED'
  | 'RESOLVED'
  | 'FALSE_POSITIVE'
export type RiskScope = 'WINDOW_DAYS' | 'PER_TRADE' | 'UTC_DAY'

/** XM-62 의 한 줄. XM-63 의 답도 이 모양이다. */
export interface RiskRule {
  code: string
  signal: string
  scope: RiskScope | string
  windowDays: number | null
  threshold: number
  thresholdUnit: string | null
  level: RiskLevel
  enabled: boolean
  temporary: boolean
  description: string | null
  source: string | null
  updatedAt: Millis | null
  updatedByAdminId: number | null
}

/** XM-63 의 본문 — 바뀐 칸만 싣는다. */
export interface RiskRulePatch {
  windowDays?: number
  threshold?: number
  level?: RiskLevel
  enabled?: boolean
}

/** 위험 항목 `RiskQueueItem` (계약 0-9). */
export interface RiskQueueItem {
  id: number
  memberId: number
  memberLoginId: string | null
  ruleCode: string
  level: RiskLevel
  scopeKey: string | null
  status: RiskQueueStatus
  assigneeAdminId: number | null
  assigneeLoginId: string | null
  evidence: unknown
  relatedOrderId: string | null
  relatedIncidentId: number | null
  hits: number
  createdAt: Millis
  updatedAt: Millis | null
  lastHitAt: Millis | null
}

/** XM-64. */
export interface MemberRisk {
  memberId: number
  loginId: string | null
  level: RiskLevel | null
  activeItems: RiskQueueItem[]
  counts: Record<string, number>
}

export interface RiskQueueEvent {
  id: number
  adminId: number | null
  adminLoginId: string | null
  fromStatus: RiskQueueStatus | null
  toStatus: RiskQueueStatus | null
  assigneeAdminId: number | null
  incidentId: number | null
  note: string | null
  at: Millis
}

/** XM-67 의 답. XM-68 의 답도 이 모양이다. */
export interface RiskQueueEntry {
  item: RiskQueueItem
  events: RiskQueueEvent[]
}

/** XM-68 의 본문 — 바뀐 칸만 싣는다. */
export interface RiskQueuePatch {
  status?: RiskQueueStatus
  assigneeAdminId?: number
  relatedIncidentId?: number
  note?: string
}

/* ── 5-5 감사 기록·설정·약관 ─────────────────────────────────────────── */

/** XM-71 의 한 줄. */
export interface AuditRow {
  id: number
  at: Millis
  /** 배경 작업은 관리자 id 를 비운다 — 화면은 「시스템」. */
  adminId: number | null
  adminLoginId: string | null
  /** 그때의 권한 이름(최고관리자면 '최고관리자'). */
  adminRole: string | null
  category: string | null
  action: string
  targetType: string | null
  targetId: string | null
  targetMemberLoginId: string | null
  targetMemberGrade: string | null
  before: unknown
  after: unknown
  reason: string | null
  relatedIncidentId: number | null
  relatedOrderId: string | null
  ip: string | null
  batchId: string | null
}

/** XM-71 의 답 — `scope` 는 서버가 건 범위다. */
export interface AuditPage extends Paged<AuditRow> {
  scope: AuditScope | string
}

export type SettingType = 'INTEGER' | 'USDE' | 'BP' | 'BOOLEAN' | 'ENUM' | 'TEXT' | 'JSON'

/** XM-83 의 한 줄. XM-84·XM-85 의 답도 이 모양이다. */
export interface Setting {
  key: string
  /** 저장 문자열. null = 미정. */
  value: string | null
  type: SettingType | string
  description: string | null
  source: string | null
  updatedAt: Millis | null
  updatedByAdminId: number | null
}

/** 약관 종류(api-XB XM-86~XM-89 `kind`) — 파트너 신청 약관·철회 약관(확인 36). */
export type TermsKind = 'PARTNER' | 'WITHDRAWAL'

/** XM-86 의 한 판. XM-87·XM-88·XM-89 의 답도 이 모양이다. */
export interface PartnerTerms {
  kind?: TermsKind | string
  version: number
  /** 영어 제목·본문 — 영어만(2026-10-02, 한글이면 400 `korean_not_allowed`). */
  title: string
  body: string
  effectiveAt: Millis
  temporary: boolean
  createdByAdminId: number | null
  createdAt: Millis
  /**
   * 시연용 한국어 판(2026-10-12 — 거래소백엔드 V544): 둘 다 있거나 둘 다 null. 거래소 앱은 서버 한국어 스위치
   * (`EXCHANGE_APP_KOREAN`)가 켜져 있고 폰이 한국어일 때만 이 글을 받는다(XA-41·XA-67). V544 전 서버는 이 칸을 주지 않는다.
   */
  titleKo?: string | null
  bodyKo?: string | null
}

/**
 * XM-100 의 한 판(지갑거래서명약관 — 거래 흐름 04 3-3). XM-101·XM-102·XM-103 의 답도 이 모양이다.
 * 거래소백엔드 `WalletTradeTerms` 그대로 — 시각은 밀리초.
 */
export interface WalletTradeTerms {
  version: number
  titleKo: string
  bodyKo: string
  titleEn: string
  bodyEn: string
  effectiveAt: Millis
  /** 넣은 관리자. 1판(V520 이 넣음)은 null. */
  createdByAdminId: number | null
  createdAt: Millis
}

/* ── 고객센터 안내·거래 자료 메일 템플릿 (XM-106~XM-113 — 거래소 01 6-35·6-36) ── */

/**
 * XM-106 의 한 판(고객센터 안내 — 거래 흐름 04 8단계). XM-107·XM-108·XM-109 의 답도 이 모양이다.
 * 거래소백엔드 `CustomerCenterGuide` 그대로 — 한국어·영어마다 연락처(전화번호·채널)·운영 시간·안내 글,
 * 시각은 밀리초.
 */
export interface CustomerCenterGuide {
  version: number
  contactKo: string
  hoursKo: string
  bodyKo: string
  contactEn: string
  hoursEn: string
  bodyEn: string
  effectiveAt: Millis
  createdByAdminId: number | null
  createdAt: Millis
}

/**
 * XM-110 의 한 판(거래 자료 메일 템플릿 — 거래 흐름 04 8단계). XM-111·XM-112·XM-113 의 답도 이 모양이다.
 * 거래소백엔드 `TradeMaterialMailTemplate` 그대로, 시각은 밀리초. 2026-10-02 부터 영어 한 벌(사장님 「일단 영어로만」 —
 * 거래소백엔드 1ab5d45): `subject`·`body` 가 메일이 쓰는 영어 글이고, `subjectKo`·`bodyKo` 는 2026-10-01 판이 가졌던
 * 한국어 한 벌의 기록일 뿐이다(메일에 쓰지 않음, 새 판은 null — 이 웹은 보이지 않는다). 앞 칸 `subjectEn`·`bodyEn` 은 없다.
 */
export interface TradeMaterialMailTemplate {
  version: number
  subject: string
  body: string
  subjectKo?: string | null
  bodyKo?: string | null
  effectiveAt: Millis
  createdByAdminId: number | null
  createdAt: Millis
}

/* ── 자동 메일 틀 (XM-119~XM-122 — 거래소 01 6-37) ──────────────────────── */

/**
 * 자동 메일 종류(XM-119 `kind` — 거래소백엔드 `AccountMailKind`, bb11762). 사장님이 말한 다섯 가지 가운데 가입 결과는
 * 글이 달라 승인·거절 두 틀이다. 서버가 종류를 더 주면 코드 원문만 보인다(표시 이름 없음).
 */
export type AccountMailKind =
  | 'signup_code' | 'recovery_code' | 'otp_reset_code' | 'temporary_password' | 'signup_approved' | 'signup_rejected'

/**
 * 자동 메일 틀 한 판(XM-119~XM-122 — 거래소백엔드 `AccountMailTemplateController.TemplateView` 그대로, 시각은 밀리초).
 * 영어 한 벌(제목·본문). `fixedLines` = 서버가 메일을 보낼 때 본문 아래 빈 줄 하나 뒤에 붙이는 줄(관리자가 고칠 수 없음 —
 * `{이름}` 자리에 그 메일의 값이 들어간다, 값이 없는 줄은 빠진다). 1판(V530)은 만든 관리자 null.
 */
export interface AccountMailTemplate {
  kind: string
  version: number
  subject: string
  body: string
  effectiveAt: Millis
  createdByAdminId: number | null
  createdAt: Millis
  fixedLines: string[]
}

/* ── 서버 점검 (XM-104·XM-105 — 거래소 01 6-34) ──────────────────────── */

/** 거래소 점검의 까닭(계약 XA-64·XM-104 `reason`). */
export type HaltReason = 'USDE_PAUSED' | 'CHAIN_DOWN' | 'SERVER_MAINTENANCE'

/**
 * XM-104 의 답. XM-105 의 답도 이 모양이다(바꾼 뒤 상태). 거래소백엔드 `MaintenanceController` 그대로.
 *
 * `maintenance`·`since` = 관리자가 켠 서버 점검. `halted`·`reason`·`message` = 앱이 보는 거래소 점검
 * (XA-64 와 같음 — 까닭을 가리지 않고, 겹치면 가장 먼저 연 점검). 그래서 서버 점검이 꺼져 있어도
 * USDE 멈춤·체인 장애 점검이 열려 있으면 `halted` 가 true 다.
 */
export interface MaintenanceState {
  maintenance: boolean
  /** 서버 점검을 켠 때(꺼져 있으면 null). */
  since: Millis | null
  halted: boolean
  reason: HaltReason | string | null
  /** 앱이 보는 점검 글(XA-64 `message`). */
  message: string | null
  /**
   * 부른 관리자가 켜고 끌 수 있는지(XM-105 와 같은 판정). 사장님 2026-10-02 「최고관리자(admin) 한 사람만 서버점검 누를
   * 수 있다.」 — 최고관리자일 때만 true(거래소백엔드 5758ccc, 그 전 11245e8 은 총괄 관리자만). 이 웹은 이 값이 true 일
   * 때만 켜기·끄기 단추를 보인다. 앞 판 서버(칸 없음)면 단추를 보이지 않는다.
   */
  canToggle?: boolean
  serverTime: Millis
}

/* ── 보증금 단계 표 (XM-93~XM-95 — R-XB-01, R-XAD-01) ─────────────── */

/** XM-93 의 한 줄. XM-94 의 답도 이 모양이다. */
export interface PartnerTier {
  tierId: number
  members: number
  /** 설정 금액(내부 단위). */
  deposit: Units
  /** false = 은퇴(새 신청·단계 올리기에 안 씀). */
  active: boolean
  createdAt: Millis | null
  createdBy: number | null
  retiredAt: Millis | null
  retiredBy: number | null
  /** 이 줄을 고른 마스터 수. */
  mastersUsing: number | null
}

/**
 * XM-46(고침) 월 사용료 한 줄 — `via` = first_deposit·deduct_fee·settle.
 *
 * 2026-10-12 (거래소백엔드 V542 — XM-96 답): `amount` = 뺀 합계, 그 나눔 `fixedFee`(고정)·`tradeCount`(회원 완료 거래,
 * `tradesFrom` 부터 `periodStart` 전까지)·`tradeFee`(거래 횟수 수수료), 그 달이 시작할 때의 요율 `tradeBlockSize`·
 * `tradeBlockFee`(첫 달과 그 전 달은 null). 옛 서버면 없다.
 */
export interface FeeMonth {
  seq: number
  periodStart: Millis | string | null
  dueAt: Millis | string | null
  amount: Units
  status: string
  chainDate: string | null
  paidAt: Millis | null
  via: string | null
  fixedFee?: Units | null
  tradeCount?: number | null
  tradeFee?: Units | null
  tradesFrom?: Millis | null
  tradeBlockSize?: number | null
  tradeBlockFee?: Units | null
}

/**
 * XM-97 — 연 단위 보관(R-XB-26) 현황. 모양은 거래소백엔드 `YearlyArchive.Overview`
 * (`adminapi/records/YearlyArchive.java`) 그대로.
 */
export interface ArchiveOverview {
  /** 운영 DB 에 두는 해 [올해, 작년](UTC — 확인 42). */
  operationalYears: number[]
  /** 보관할 수 있는 마지막 해(올해 − 2). 이 해까지만 XM-98 을 받는다. */
  archivableThrough: number
  /** 함께 옮기는 표 묶음(`ArchiveGroups.GroupView`). */
  groups: ArchiveGroup[]
  /** 최근 50개 보관 실행, 새것 먼저. */
  runs: ArchiveRun[]
}

export interface ArchiveGroup {
  code: string
  description: string
  tables: string[]
}

export type ArchiveRunStatus = 'RUNNING' | 'DONE' | 'FAILED'

/** XM-97 `runs` 의 한 줄. XM-98 의 답도 이 모양이다(`YearlyArchive.Run`). */
export interface ArchiveRun {
  runId: number
  year: number
  status: ArchiveRunStatus | string
  /** 요청한 관리자. 매일 작업이 돌린 실행이면 null. */
  requestedByAdminId: number | null
  startedAt: Millis | null
  finishedAt: Millis | null
  /** 운영 표에서 옮긴 행 수(묶음마다 더한 값). 진행 중이면 null. */
  movedRows: number | null
  error: string | null
  parts: ArchivePart[]
}

/** 실행의 표별 결과(`YearlyArchive.PartResult`). */
export interface ArchivePart {
  group: string
  partNo: number
  table: string
  rows: number
  /** 옮긴 행 글의 md5 — 옮기기 전 읽은 행과 같음을 확인한 값. 옮긴 행이 없으면 null. */
  checksum: string | null
}

/** XM-99 — 보관소의 행(`YearlyArchive.ArchivedRows`). 행은 DB 열 이름 그대로(`row_to_json`). */
export interface ArchivedRows {
  year: number
  table: string
  page: number
  size: number
  total: number
  rows: Record<string, unknown>[]
}

/* ── 5-5 보존·파기·보관 파일 (XM-76~XM-82) ─────────────────────────── */

/** XM-77 의 한 줄. XM-78 의 답도 이 모양이다. */
export interface RetentionPolicy {
  /** 파기 대상 코드(서버가 정한 이름 — 예 `trades`). */
  target: string
  description: string | null
  retentionYears: number
  enabled: boolean
  /** 이 서버에 그 대상의 파기 코드가 있는가. 없으면 세기·승인이 503. */
  connected: boolean
  updatedAt: Millis | null
  updatedByAdminId: number | null
}

export type RetentionRunStatus = 'PENDING_APPROVAL' | 'EXECUTED' | 'REJECTED' | 'FAILED'

/** XM-79 의 답(파기 실행 한 건). XM-80 의 줄, XM-81·XM-82 의 답도 이 모양이다. */
export interface RetentionRun {
  id: number
  target: string
  retentionYears: number
  /** 이 시각보다 오래된 기록이 대상. */
  cutoff: Millis
  /** 셀 때 센 줄 수. */
  counted: number
  status: RetentionRunStatus | string
  requestedByAdminId: number | null
  requestedAt: Millis | null
  decidedByAdminId: number | null
  decidedAt: Millis | null
  /** 승인해 실제로 지운 줄 수. 아직이면 null. */
  deletedCount: number | null
  error: string | null
}

/** XM-76 의 한 줄 — 회원 로그 주 단위 보관 파일. */
export interface UserLogArchive {
  weekStart: string
  fileName: string
  rowCount: number
  firstLogId: number | null
  lastLogId: number | null
  sha256: string
  createdAt: Millis
}

/* ── 화면 표시명 (명세 4-2) ───────────────────────────────────────────── */

/** 최고관리자는 권한 이름이 없다 — 머리글·내 계정에 이 말을 쓴다. */
export const SUPER_ADMIN_LABEL = 'Super admin'

export const ADMIN_STATUS_LABEL: Record<AdminStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  SUSPENDED: 'Suspended',
}

export const AUDIT_SCOPE_LABEL: Record<AuditScope, string> = {
  ALL: 'All',
  OWN_AND_RISK_INCIDENT: 'Own + risk & incidents',
  OWN_OPERATIONS: 'Own operations',
}

export const GRADE_LABEL: Record<MemberGrade, string> = {
  MASTER: 'Master member',
  SPECIAL: 'Special member',
  GENERAL: 'General member',
}

export const MEMBER_STATUS_LABEL: Record<MemberStatus, string> = {
  ACTIVE: 'Normal',
  SUSPENDED: 'Suspended',
  WITHDRAWN: 'Account closed',
}

export const KYC_STATUS_LABEL: Record<KycStatus, string> = {
  NOT_STARTED: 'Not started',
  PENDING: 'Under review',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
}

export const KYC_PROVIDER_LABEL: Record<KycProvider, string> = {
  SUMSUB: 'SUMSUB',
  TEMPORARY: 'Temporary provider',
}

/** 가입 신청 상태(2026-10-12 — KYC 인증 메뉴). 영어만(관리자 웹). */
export const SIGNUP_STATUS_LABEL: Record<SignupStatus, string> = {
  MANUAL_REVIEW: 'Manual review requested',
  KYC_REVIEW: 'Waiting for the identity check',
  KYC_REJECTED: 'Identity check not approved',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
}

export const SIGNUP_KYC_STATUS_LABEL: Record<SignupKycStatus, string> = {
  STARTED: 'Not submitted yet',
  UNDER_REVIEW: 'Under review',
  RESUBMIT: 'Resubmission requested',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected (final)',
}

export const APPROVED_BY_LABEL: Record<'KYC' | 'ADMIN', string> = {
  KYC: 'Identity check (no admin)',
  ADMIN: 'Admin (manual review)',
}

export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
}

export const REVOCATION_STATUS_LABEL: Record<RevocationStatus, string> = {
  REQUESTED: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Denied',
}

export const PARTNER_STATUS_LABEL: Record<PartnerStatus, string> = {
  AWAITING_DEPOSIT: 'Awaiting first deposit',
  ACTIVE: 'Active',
  WITHDRAWAL_PENDING: 'Withdrawal pending',
  WITHDRAWN: 'Withdrawn',
  REVOKED: 'Revoked',
  CANCELLED_UNPAID: 'Approval cancelled (unpaid)',
}

export const WITHDRAWAL_STATUS_LABEL: Record<WithdrawalStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  SUPERSEDED: 'Replaced by a new request',
}

export const WARNING_LEVEL_LABEL: Record<number, string> = {
  0: 'None',
  1: 'Level 1',
  2: 'Level 2',
  3: 'Suspended',
}

export const CAPACITY_DIRECTION_LABEL: Record<CapacityDirection, string> = {
  INCREASE: 'Increase',
  DECREASE: 'Decrease',
}

export const CAPACITY_STATUS_LABEL: Record<CapacityChangeStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
}

export const INCIDENT_STATUS_LABEL: Record<IncidentStatus, string> = {
  OPEN: 'Received',
  PROCESSING: 'Processing',
  CLOSED: 'Closed',
}

/** 처리 창의 두 가지 — 이름은 사장님 원문 9 「거래완료 또는 거래취소」 그대로. */
export const INCIDENT_ACTION_LABEL: Record<IncidentAction, string> = {
  CONFIRM: 'Complete trade',
  CANCEL: 'Cancel trade',
}

/**
 * 처리 기록(XM-58 `events[].action`)의 표시 이름. 2026-10-01 전에 보낸 옛 처리 셋은 기록을 읽을 때만
 * 쓴다 — 처리 창에서는 고를 수 없다(서버도 400).
 */
export const INCIDENT_EVENT_ACTION_LABEL: Record<string, string> = {
  ...INCIDENT_ACTION_LABEL,
  RESOLVE_APPROVE: 'Approve incident ruling',
  RESOLVE_REJECT: 'Reject incident ruling',
  EXPIRE: 'Mark expired',
}

/**
 * 사진 판독 결과 — 일치·상대 증빙과 금액이 맞지 않음·못 읽음은 사장님 말(질문 23), 서로 맞지 않음·상대 증빙
 * 기다림은 서버 메일 글과 같은 제안(거래소 03 52번 ⑭).
 */
export const PROOF_RESULT_LABEL: Record<ProofResult, string> = {
  clear: 'Match',
  amount_mismatch: "The amount does not match the other party's proof",
  unreadable: 'Could not be read',
  anomaly: "Does not match the other party's proof",
  waiting: "Waiting for the other party's proof",
}

/** 거래가 취소로 끝난 까닭(관리자 거래 보기 `cancelCause`). */
export const CANCEL_CAUSE_LABEL: Record<string, string> = {
  ADMIN: 'Cancelled by admin',
  BOTH_PARTIES: 'Cancelled by both parties',
  SIGN_TIMEOUT: 'Signing time passed',
  SIGN_REFUSED: 'Signing refused',
}

/** 거래 대화의 보낸 쪽. */
export const CHAT_SENDER_LABEL: Record<string, string> = {
  system: 'System',
  seller: 'Seller',
  buyer: 'Buyer',
}

/** 자료 메일 받는 분 — 사고 상세의 「판매자 (코인 보냄)」·「구매자 (코인 받음)」 줄과 같은 말. */
export const MATERIAL_MAIL_ROLE_LABEL: Record<string, string> = {
  seller: 'Seller',
  buyer: 'Buyer',
}

/** 자료 메일 언어 — 사장님 말 「언어(한국어/English)」 그대로. */
export const MAIL_LANGUAGE_LABEL: Record<MailLanguage, string> = {
  ko: 'Korean',
  en: 'English',
}

export const MATERIAL_MAIL_STATUS_LABEL: Record<MaterialMailStatus, string> = {
  SENT: 'Sent',
  FAILED: 'Failed',
  SENDING: 'Sending',
}

export const TRADE_STAGE_LABEL: Record<TradeStage, string> = {
  awaiting_signatures: 'Awaiting signatures',
  awaiting_proof: 'Awaiting proof',
  proof_partial: 'Partial proof',
  inspecting: 'Inspecting',
  incident: 'Incident',
  completing: 'Confirming completion',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/** `start_sell` = 일반 회원이 삼 = 구매(붉은색), `start_buy` = 일반 회원이 팖 = 판매(청록색). */
export const DEPOSIT_TYPE_LABEL: Record<DepositType, string> = {
  start_sell: 'Buy',
  start_buy: 'Sell',
}

export const TRADE_SOURCE_LABEL: Record<TradeSource, string> = {
  APP: 'App',
  EGGPAY: 'Easy payment',
}

/** 증빙의 역할 — 코인 방향이다(레거시 이름 그대로). */
export const PROOF_ROLE_LABEL: Record<string, string> = {
  seller: 'Coin sender',
  buyer: 'Coin receiver',
}

export const VERDICT_LABEL: Record<string, string> = {
  clear: 'Normal',
  anomaly: 'Anomaly',
}

export const GRADE_REASON_LABEL: Record<GradeChangeReason, string> = {
  REFERRAL_CODE_REGISTERED: 'Joined by referral code',
  MASTER_APPROVED_JOIN: 'Join request approved',
  MASTER_EXPELLED: 'Expelled from the organization',
  ADMIN_EXPELLED: 'Expelled by an admin',
  PARTNER_APPROVED: 'Partner application approved',
  WITHDRAWAL_APPROVED: 'Collateral withdrawn',
  UNPAID_APPROVAL_CANCELLED: 'Approval cancelled (first collateral unpaid)',
  MASTER_REVOKED: 'Master status revoked',
  DIRECT_CHANGE: 'Changed directly by an admin',
  CAPACITY_DEPOSIT_CHANGED: 'Per-seat collateral changed',
}

export const RISK_LEVEL_LABEL: Record<RiskLevel, string> = {
  CAUTION: 'Caution',
  SUSPICIOUS: 'Suspicious',
  HIGH_RISK: 'High risk',
}

export const RISK_QUEUE_STATUS_LABEL: Record<RiskQueueStatus, string> = {
  NEW: 'New',
  UNDER_REVIEW: 'Under review',
  ESCALATED: 'Escalated',
  ON_HOLD: 'On hold',
  RESTRICTED: 'Restricted',
  RESOLVED: 'Resolved',
  FALSE_POSITIVE: 'False positive',
}

export const RISK_SCOPE_LABEL: Record<RiskScope, string> = {
  WINDOW_DAYS: 'Within the period',
  PER_TRADE: 'Per trade',
  UTC_DAY: 'One day (UTC)',
}

export const SETTING_TYPE_LABEL: Record<SettingType, string> = {
  INTEGER: 'Integer',
  USDE: 'Amount (USDE)',
  BP: 'Rate',
  BOOLEAN: 'On/Off',
  ENUM: 'Choice',
  TEXT: 'Text',
  JSON: 'JSON',
}

export const RETENTION_RUN_STATUS_LABEL: Record<RetentionRunStatus, string> = {
  PENDING_APPROVAL: 'Awaiting approval',
  EXECUTED: 'Destroyed',
  REJECTED: 'Rejected',
  FAILED: 'Failed',
}

export const ARCHIVE_RUN_STATUS_LABEL: Record<ArchiveRunStatus, string> = {
  RUNNING: 'Running',
  DONE: 'Done',
  FAILED: 'Failed',
}

export const TERMS_KIND_LABEL: Record<TermsKind, string> = {
  PARTNER: 'Partner terms',
  WITHDRAWAL: 'Withdrawal terms',
}

/** 자동 메일 종류(XM-119 `kind`) — 이름은 제안(03 24번 — 사장님 확인 전). 차례는 서버 차례(XM-120). */
export const ACCOUNT_MAIL_KIND_LABEL: Record<string, string> = {
  signup_code: 'Sign-up email code',
  recovery_code: 'ID and password recovery code',
  otp_reset_code: 'OTP re-registration code',
  temporary_password: 'Temporary password',
  signup_approved: 'Sign-up approved',
  signup_rejected: 'Sign-up rejected',
}

/** 거래소 점검 까닭(XM-104 `reason`). */
export const HALT_REASON_LABEL: Record<HaltReason, string> = {
  USDE_PAUSED: 'USDE paused',
  CHAIN_DOWN: 'Chain outage',
  SERVER_MAINTENANCE: 'Server maintenance',
}

export const FEE_VIA_LABEL: Record<string, string> = {
  first_deposit: 'At first deposit',
  deduct_fee: 'Monthly deduction',
  settle: 'At settlement',
}
